// import { Box, Heading, Text, Stack, VStack, HStack, Divider } from "@chakra-ui/layout";
// import { Button, FormControl, FormLabel, Image, NumberDecrementStepper, NumberIncrementStepper, NumberInput, NumberInputField, NumberInputStepper, Radio, RadioGroup, useColorModeValue } from "@chakra-ui/react";
// import { useForm } from "react-hook-form";
// import { ChangeEvent, useEffect, useState } from "react";

// import { CorporationIdentifiersDictionary } from "doxecure-commons/lib/doxecureTypes";
// import { MollieMethod } from "doxecure-commons/lib/mollieTypes";
// import { $count, $keys, $length, $ok } from "foundation-ts/commons";
// import { TSCountry } from "foundation-ts/tscountry";
// import { Countries, Nullable } from "foundation-ts/types";

// import { G1Button } from "@components/G1Button";
// import { CorporationForm } from "@/components/CorporationForm/CorporationForm";
// import { G1Input } from "@components/G1Input";
// import MollieCardForm from "@/components/Register/MollieCardForm";
// import MollieMandateForm from "@/components/Register/MollieMandateForm";
// import VersionDisplay from "@components/VersionDisplay";
// import { config } from "@/config/config";
// import useAvailableCorporationIdentifiers from "@/hooks/useAvailableCorporationIdentifiers";
// import useCountries from "@/hooks/useCountries";
// import { useDevice } from "@/hooks/useDevice";
// import { $g1Color, $g1IconURL } from "@/theme/G1Style";
// import { G1Icon } from "@/utils/icons";
// import { MollieService } from "@/services/mollie.service";

// type Step = "account" | "subscription" | "billing" | "confirmation";

import { Box, Heading, Stack, VStack } from "@chakra-ui/layout";
import { Image } from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { ChangeEvent, useEffect, useState } from "react";

import { $count, $keys, $ok } from "foundation-ts/commons";
import { Countries, Nullable } from "foundation-ts/types";
import { CorporationIdentifiersDictionary } from "doxecure-commons/lib/doxecureTypes";
import { MollieMethod } from "doxecure-commons/lib/mollieTypes";
import { TSCountry } from "foundation-ts/tscountry";

import VersionDisplay from "@components/VersionDisplay";
import { Step, SubscriptionType, RegisterFormValues } from "@components/Register/RegisterTypes";
import { config } from "@/config/config";
import useAvailableCorporationIdentifiers from "@/hooks/useAvailableCorporationIdentifiers";
import useCountries from "@/hooks/useCountries";
import { $g1Color, $g1IconURL } from "@/theme/G1Style";
import { MollieService } from "@/services/mollie.service";

import { SubscriptionStep } from "@components/Register/SubscriptionStep";
import ConfirmationStep from "@/components/Register/ConfirmationStep";
import BillingStep from "@/components/Register/BillingStep";
import AccountStep from "@/components/Register/AccountStep";
import RegisterStepper from "@/components/Register/RegisterStepper";

const mollieService = new MollieService();
const STEP_ORDER: Step[] = ["subscription", "account", "billing", "confirmation"];

export const RegisterView = () => {
    const { data: countries } = useCountries();
    const { data: availableCorporationIdentifiers } = useAvailableCorporationIdentifiers(countries, $count(countries) > 0);

    const [selectedSubscription, setSelectedSubscription] = useState<Nullable<SubscriptionType>>(null);
    const [isAloneUser, setIsAloneUser] = useState<Nullable<boolean>>(null);
    const [nbCollaborators, setNbCollaborators] = useState(0);
    const [amount, setAmount] = useState<Nullable<number>>(null);
    const [partnerCode, setPartnerCode] = useState("");
    const [corporationIdentifiersDict, setCorporationIdentifiersDict] = useState<CorporationIdentifiersDictionary>({});
    const [tvaIntra, setTvaIntra] = useState("");
    const [paymentMethods, setPaymentMethods] = useState<Nullable<MollieMethod[]>>(null);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("");
    const [currentStep, setCurrentStep] = useState<Step>("subscription");
    const [completedSteps, setCompletedSteps] = useState<Set<Step>>(new Set());

    const { getValues, register, setValue, formState: { errors } } =
        useForm<RegisterFormValues>({ mode: "onChange", reValidateMode: "onChange" });

    const planLabel = `${selectedSubscription === "limited" ? "Abonnement 60" : "Abonnement illimité"} · ${amount}€ HT/an`;


    const validateStep = (step: Step): boolean => {
        switch (step) {
            case "subscription": return $ok(selectedSubscription) && (!!isAloneUser || nbCollaborators > 0);
            case "account":      return true;
            case "billing":      return true;
            default:             return false;
        }
    };

    const handleNextStep = async (current: Step, next: Step): Promise<void> => {
        if (!validateStep(current)) return;
        setCompletedSteps(prev => new Set(prev).add(current));
        setCurrentStep(next);
        if (next === "confirmation") {
            const methods = await mollieService.getPaymentMethods();
            setPaymentMethods(methods);
        }
    };

    const handleSubscriptionChoice = (subscription: SubscriptionType): void => {
        const isSame = subscription === selectedSubscription;
        setSelectedSubscription(isSame ? null : subscription);
        if (isSame) {
            setAmount(null);
            setIsAloneUser(null);
        } else {
            setAmount(subscription === "limited" ? 109 : 180);
        }
    };

    const handleCollaboratorsChange = (_valueAsString: string, valueAsNumber: number): void => {
        setNbCollaborators(valueAsNumber);
        setAmount((selectedSubscription === "limited" ? 109 : 180) + valueAsNumber * 60);
    };

    const handleCorporationChange = (e: any): void => {
        setValue(e.target.name, e.target.value);
    };

    const handleCountryChange = (e: ChangeEvent<HTMLSelectElement>): void => {
        const newCountry = TSCountry.country(e.currentTarget.value)?.alpha2Code;
        if ($ok(newCountry)) {
            const newDict = availableCorporationIdentifiers?.get(newCountry!);
            if ($ok(newDict)) {
                setCorporationIdentifiersDict(newDict!);
                setValue("organizationIdentifierType", $keys(newDict).first());
            }
        }
        setValue("organizationCountry", e.currentTarget.value);
    };

    const handleCardSuccess = async (token: string): Promise<void> => {
        // TODO : POST /api/subscriptions/first-payment
        console.log("CB token :", token);
    };

    const handleMandateSuccess = async (consumerAccount: string, consumerName: string): Promise<void> => {
        // TODO : POST /api/subscriptions/create-mandate
        console.log("Mandat SEPA :", consumerAccount, consumerName);
    };

    useEffect(() => {
        const country = TSCountry.country(Countries.FR)?.alpha2Code;
        if ($ok(country)) {
            const newDict = availableCorporationIdentifiers?.get(country!);
            if ($ok(newDict)) { setCorporationIdentifiersDict(newDict!); }
        }
    }, [availableCorporationIdentifiers]);

    const steps = [
        {
            key: "subscription" as Step,
            label: "Choix de votre abonnement",
            element: (
                <SubscriptionStep
                    selectedSubscription={selectedSubscription}
                    isAloneUser={isAloneUser}
                    nbCollaborators={nbCollaborators}
                    partnerCode={partnerCode}
                    onSubscriptionChoice={handleSubscriptionChoice}
                    onAloneUserChange={setIsAloneUser}
                    onCollaboratorsChange={handleCollaboratorsChange}
                    onPartnerCodeChange={setPartnerCode}
                />
            ),
        },
        {
            key: "account" as Step,
            label: "Informations du compte",
            element: (
                <AccountStep
                    isAloneUser={isAloneUser}
                    corporationIdentifiersDict={corporationIdentifiersDict}
                    countries={countries}
                    errors={errors}
                    register={register}
                    onCorporationChange={handleCorporationChange}
                    onCountryChange={handleCountryChange}
                />
            ),
        },
        {
            key: "billing" as Step,
            label: "Coordonnées de facturation",
            element: (
                <BillingStep
                    tvaIntra={tvaIntra}
                    onTvaIntraChange={setTvaIntra}
                />
            ),
        },
        {
            key: "confirmation" as Step,
            label: "Confirmation",
            element: (
                <ConfirmationStep
                    paymentMethods={paymentMethods}
                    selectedPaymentMethod={selectedPaymentMethod}
                    planLabel={planLabel}
                    companyName={config.app.title}
                    mollieProfileId={config.app.mollie.profileId!}
                    testMode
                    onMethodChange={setSelectedPaymentMethod}
                    onCardSuccess={handleCardSuccess}
                    onMandateSuccess={handleMandateSuccess}
                />
            ),
        },
    ];

    // ── Render ───────────────────────────────────────────────────────────────

    return (
        <Box py="5%">
            <Box
                color={$g1Color("connection.logo.write")}
                bg={$g1Color("connection.logo.bg")}
                paddingTop="8" paddingBottom="2" px="8"
                maxW="3xl" mx="auto"
                rounded={{ base: "0", md: "2xl" }}
            >
                <VStack spacing="4" align="stretch">
                    <Stack direction="row" alignItems="center" spacing="2">
                        <Image src={$g1IconURL("connection.logo.iconurl")} alt="Application logo" boxSize="72px" />
                        <Heading as="h1" textAlign="center" size="lg" fontWeight="bold">
                            {config.app.title}
                        </Heading>
                    </Stack>

                    <RegisterStepper
                        steps={steps}
                        stepOrder={STEP_ORDER}
                        currentStep={currentStep}
                        completedSteps={completedSteps}
                        validateStep={validateStep}
                        onStepClick={setCurrentStep}
                        onNextStep={handleNextStep}
                        summaryProps={{
                            selectedSubscription,
                            isAloneUser,
                            nbCollaborators,
                            amount,
                            partnerCode,
                            lastname:               getValues("lastname"),
                            firstname:              getValues("firstname"),
                            email:                  getValues("email"),
                            organizationName:       getValues("organizationName"),
                            organizationIdentifier: getValues("organizationIdentifier"),
                            tvaIntra
                        }}
                    />

                    <VersionDisplay />
                </VStack>
            </Box>
        </Box>
    );
}

// export const RegisterView = () => {
//     const { data: countries } = useCountries();
//     const { data: availableCorporationIdentifiers } = useAvailableCorporationIdentifiers(countries, $count(countries) > 0);
//     const { isMOBILE } = useDevice() ;
//     const [selectedSubscription, setSelectedSubscription] = useState<"limited" | "unlimited" | null>(null);
//     const [isAloneUser, setIsAloneUser] = useState<Nullable<boolean>>(null);
//     const [nbCollaborators, setNbCollaborators] = useState(0);
//     const [amount, setAmount] = useState<Nullable<number>>(null);
//     // const [isSameContactFacturation, setIsSameContactFacturation] = useState<Nullable<boolean>>(null);
//     const [corporationIdentifiersDict, setCorporationIdentifiersDict] = useState<CorporationIdentifiersDictionary>({}) ;
//     const [currentStep, setCurrentStep] = useState<Step>("subscription");
//     const [completedSteps, setCompletedSteps] = useState<Set<Step>>(new Set());
//     const [paymentMethods, setPaymentMethods] = useState<Nullable<MollieMethod[]>>(null);
//     const [partnerCode, setPartnerCode] = useState<string>("");
//     const [tvaIntra, setTvaIntra] = useState<string>("");
//     const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('');
//     const limitedBorderColor = useColorModeValue("blue.400", "blue.100");
//     const limitedBgColor = useColorModeValue("blue.50", "blue.400");
//     const unlimitedBorderColor = useColorModeValue("green.400", "green.50");
//     const unlimitedBgColor = useColorModeValue("green.50", "green.400");
//     const limitedActiveTextColor = useColorModeValue("blue.600", "blue.100");
//     const unlimitedActiveTextColor = useColorModeValue("green.600", "green.100");
//     const textColor = useColorModeValue("gray.100", "gray.400");
//     const btnTitleColor = useColorModeValue("gray.100", "gray.400");
//     const {
//         getValues,
//         register,
//         setValue,
//         trigger,
//         watch,
//         formState: { errors, isValid },
//     } = useForm<any>({
//         mode: "onChange",
//         reValidateMode: "onChange",
//     }) ;

//     const stepOrder: Step[] = ["subscription", "account", "billing", "confirmation"];
//     const mollieService = new MollieService();

//     const validateStep = (step: Step): boolean => {
//         switch (step) {
//             case "account":
//             return true;
//             case "subscription":
//                 return $ok(selectedSubscription) && (!!isAloneUser || nbCollaborators > 0);
//             case "billing":
//                 return true;
//             default:
//                 return false;
//         }
//     };

//     const handleNextStep = async (current: Step, next: Step) => {
//         if (validateStep(current)) {
//             setCompletedSteps(prev => new Set(prev).add(current));
//             setCurrentStep(next);
//         }
//         if (next === "confirmation") {
//             const paymentMethods = await mollieService.getPaymentMethods();
//             setPaymentMethods(paymentMethods);
//         }
//     };

//     const handleSubscriptionChoice = (subscription: "limited" | "unlimited") => {
//         setSelectedSubscription(prev => subscription === prev ? null : subscription);
//         if (subscription === selectedSubscription) {
//             setAmount(null);
//             setIsAloneUser(null);
//         } else {
//             setAmount(subscription === "limited" ? 109 : 180);
//         }
//     };

//     const handleAddCollaborators = (_valueAsString: string, valueAsNumber: number) => {
//         setNbCollaborators(valueAsNumber);
//         setAmount((selectedSubscription === "limited" ? 109 : 180) + valueAsNumber*60);
//     }

//     const handleCorporationChange = (e) => {
//         const { name, value } = e.target;
//         setValue(name, value);
//     }

//     const handleCountryChange = (e: ChangeEvent<HTMLSelectElement>) => {
//         const newCountry = TSCountry.country(e.currentTarget.value)?.alpha2Code ;
//         if ($ok(newCountry)) {
//             const newDict = availableCorporationIdentifiers?.get(newCountry!) ;
//             if ($ok(newDict)) { 
//                 setCorporationIdentifiersDict(newDict!) ;
//                 setValue("organizationIdentifierType", $keys(newDict).first()) ;
//             }
//         }
//         setValue("organizationCountry", e.currentTarget.value) ;
//     }

//     // const handleInvoiceContactChoice = (same: boolean) => {
//     //     setIsSameContactFacturation(same);
//     //     setValue("ctLastname", same ? watch("lastname") : "");
//     //     setValue("ctFirstname", same ?watch("firstname") : "");
//     //     setValue("ctEmail", same ? watch("email") : "");
//     // }

//     const handlePaymentSuccess = async (_token: string, _cardHolder: string): Promise<void> => {
//         alert("OK");
//     }

//     const defineSoloUserElement = (
//         <HStack spacing={4} alignSelf="flex-start">
//             <Text>Etes-vous le seul utilisateur du parapheur ?</Text>
//             <Button
//                 size="sm"
//                 borderWidth="2px"
//                 borderRadius="lg"
//                 borderColor={isAloneUser === true ? limitedBorderColor : "gray.200"}
//                 bg={isAloneUser === true ? limitedBgColor : "transparent"}
//                 color={isAloneUser === true ? limitedActiveTextColor : textColor}
//                 _hover={{ borderColor: limitedBorderColor, bg: limitedBgColor, color: limitedActiveTextColor }}
//                 transition="all 0.2s"
//                 onClick={() => setIsAloneUser(true)}
//             >
//                 Oui
//             </Button>
//             <Button
//                 size="sm"
//                 borderWidth="2px"
//                 borderRadius="lg"
//                 borderColor={isAloneUser === false ? limitedBorderColor : "gray.200"}
//                 bg={isAloneUser === false ? limitedBgColor : "transparent"}
//                 color={isAloneUser === false ? limitedActiveTextColor : textColor}
//                 _hover={{ borderColor: limitedBorderColor, bg: limitedBgColor, color: limitedActiveTextColor }}
//                 transition="all 0.2s"
//                 onClick={() => setIsAloneUser(false)}
//             >
//                 Non
//             </Button>
//         </HStack>
//     );

//     const defineNbUsersElement = (
//         <HStack align="stretch" alignSelf="flex-start" alignItems="center" mt={2}> 
//             <Text>Nombre de collaborateurs supplémentaires :</Text>
//             <NumberInput value={nbCollaborators} min={0} step={1} onChange={handleAddCollaborators} size="md">
//                 <NumberInputField
//                     minW="70px"
//                     width={`${Math.max(70, nbCollaborators.toString().length * 16 + 40)}px`}
//                     textAlign="center"
//                     fontWeight="semibold"
//                     fontSize="md"
//                     borderRadius="lg"
//                     borderWidth="2px"
//                     bg={$g1Color(`form.field.bg`)}
//                     color={$g1Color(`form.field.write`)}
//                     borderColor={$g1Color(`form.field.border`)}
//                     _hover={{ borderColor: $g1Color(`form.field.hover.border`) }}
//                     _focus={{ borderColor: $g1Color(`form.field.select.border`), boxShadow: "none" }}
//                 />
//                 <NumberInputStepper borderWidth="0" flexDirection="column" width="28px">
//                     <NumberIncrementStepper
//                         border="none"
//                         borderRadius="md"
//                         mx="1"
//                         my="0.5"
//                         bg="blue.400"
//                         color="white"
//                         fontSize="xs"
//                         fontWeight="bold"
//                         _hover={{ bg: "blue.500", transform: "scale(1.1)" }}
//                         _active={{ bg: "blue.600", transform: "scale(0.95)" }}
//                         transition="all 0.15s"
//                     >
//                         ＋
//                     </NumberIncrementStepper>
//                     <NumberDecrementStepper
//                         border="none"
//                         borderRadius="md"
//                         mx="1"
//                         my="0.5"
//                         bg="gray.200"
//                         color="gray.600"
//                         fontSize="xs"
//                         fontWeight="bold"
//                         _hover={{ bg: "gray.300", transform: "scale(1.1)" }}
//                         _active={{ bg: "gray.400", transform: "scale(0.95)" }}
//                         transition="all 0.15s"
//                     >
//                         －
//                     </NumberDecrementStepper>
//                 </NumberInputStepper>
//             </NumberInput>
//             <Text>* 60€ HT</Text>
//         </HStack>
//     );

//     const selectSubscriptionTypeElement = (
//         <VStack spacing={3}>
//             <HStack spacing={4} align="stretch" width="100%">
//                 <Box
//                     as="button"
//                     p={isMOBILE ? 1 : 5}
//                     flex="1"
//                     borderWidth="2px"
//                     borderRadius="lg"
//                     borderColor={selectedSubscription === "limited" ? limitedBorderColor : "gray.200"}
//                     bg={selectedSubscription === "limited" ? limitedBgColor : "transparent"}
//                     _hover={{ borderColor: limitedBorderColor, bg: limitedBgColor, "& .btn-title": { color: limitedActiveTextColor }, "& .btn-text": { color: limitedActiveTextColor } }}
//                     transition="all 0.2s"
//                     onClick={() => handleSubscriptionChoice("limited")}
//                 >
//                     <HStack justifyContent="center">
//                         <Text
//                             className="btn-title"
//                             fontSize={["sm","2xl"]}
//                             fontWeight="bold"
//                             mb={2}
//                             color={selectedSubscription === "limited" ? limitedActiveTextColor : btnTitleColor}
//                             transition="color 0.2s"
//                         >
//                             Abonnement 60
//                         </Text>
//                     </HStack>
//                     <Text className="btn-text" fontSize={["sm", "lg"]} fontWeight="bold" color={selectedSubscription === "limited" ? limitedActiveTextColor : textColor} transition="color 0.2s">109€ HT / an</Text>
//                     <HStack mt={2}><G1Icon.Check /><Text className="btn-text" fontSize={["xs", "sm"]} color={selectedSubscription === "limited" ? limitedActiveTextColor : textColor} transition="color 0.2s">Pour 1 utilisateur</Text></HStack>
//                     <HStack><G1Icon.Check /><Text className="btn-text" fontSize={["xs", "sm"]} color={selectedSubscription === "limited" ? limitedActiveTextColor : textColor} transition="color 0.2s">60 signatures incluses</Text></HStack>
//                     <HStack><G1Icon.Check /><Text className="btn-text" fontSize={["xs", "sm"]} color={selectedSubscription === "limited" ? limitedActiveTextColor : textColor} transition="color 0.2s">Inclus 1 mois gratuit</Text></HStack>
//                 </Box>

//                 <Box
//                     as="button"
//                     p={isMOBILE ? 1 : 5}
//                     flex="1"
//                     borderWidth="2px"
//                     borderRadius="lg"
//                     borderColor={selectedSubscription === "unlimited" ? unlimitedBorderColor : "gray.200"}
//                     bg={selectedSubscription === "unlimited" ? unlimitedBgColor : "transparent"}
//                     _hover={{ borderColor: unlimitedBorderColor, bg: unlimitedBgColor, "& .btn-title": { color: unlimitedActiveTextColor }, "& .btn-text": { color: unlimitedActiveTextColor } }}
//                     transition="all 0.2s"
//                     onClick={() => handleSubscriptionChoice("unlimited")}
//                 >
//                     <HStack justifyContent="center">
//                         <Text
//                             className="btn-title"
//                             fontSize={["sm","2xl"]}
//                             fontWeight="bold"
//                             mb={2}
//                             color={selectedSubscription === "unlimited" ? unlimitedActiveTextColor : btnTitleColor}
//                             transition="color 0.2s"
//                         >
//                             Abonnement illimité
//                         </Text>
//                     </HStack>
//                     <Text className="btn-text" fontSize={["sm", "lg"]} fontWeight="bold" color={selectedSubscription === "unlimited" ? unlimitedActiveTextColor : textColor} transition="color 0.2s">180€ HT / an</Text>
//                     <HStack mt={2}><G1Icon.Check /><Text className="btn-text" fontSize={["xs", "sm"]} color={selectedSubscription === "unlimited" ? unlimitedActiveTextColor : textColor} transition="color 0.2s">Pour 1 utilisateur</Text></HStack>
//                     <HStack><G1Icon.Check /><Text className="btn-text" fontSize={["xs", "sm"]} color={selectedSubscription === "unlimited" ? unlimitedActiveTextColor : textColor} transition="color 0.2s">Nombre de signatures illimitées</Text></HStack>
//                     <HStack><G1Icon.Check /><Text className="btn-text" fontSize={["xs", "sm"]} color={selectedSubscription === "unlimited" ? unlimitedActiveTextColor : textColor} transition="color 0.2s">Inclus 1 mois gratuit</Text></HStack>
//                 </Box>
//             </HStack>
//             { $ok(selectedSubscription) && 
//                 <HStack alignSelf="flex-start">
//                     <Text whiteSpace="nowrap" flexShrink={0}>Code partenaire :</Text>
//                     <G1Input component="connection.form" value={partnerCode} textTransform="uppercase" onChange={(e) => setPartnerCode(e.target.value)} />
//                 </HStack>
//             }
//             { $ok(selectedSubscription) && defineSoloUserElement }
//             { $ok(selectedSubscription) && isAloneUser === false && defineNbUsersElement }
//         </VStack>
//     );

//     const billingFormElement = (
//         <VStack spacing={4}>
//             <HStack alignSelf="flex-start">
//                 <Text whiteSpace="nowrap" flexShrink={0} mr={0}>Numéro de TVA intracommunautaire</Text><Text color="red.300">*</Text>
//                 <G1Input component="connection.form" marginLeft={4} autoFocus textTransform="uppercase" value={tvaIntra} onChange={(e) => setTvaIntra(e.target.value)} />
//             </HStack>
//             {/* <HStack spacing={4} alignSelf="flex-start">
//                 <Text>Etes-vous le contact facturation ?</Text>
//                 <Button
//                     size="sm"
//                     borderWidth="2px"
//                     borderRadius="lg"
//                     borderColor={isSameContactFacturation === true ? limitedBorderColor : "gray.200"}
//                     bg={isSameContactFacturation === true ? limitedBgColor : "transparent"}
//                     color={isSameContactFacturation === true ? limitedActiveTextColor : textColor}
//                     _hover={{ borderColor: limitedBorderColor, bg: limitedBgColor, color: limitedActiveTextColor }}
//                     transition="all 0.2s"
//                     onClick={() => handleInvoiceContactChoice(true)}
//                 >
//                     Oui
//                 </Button>
//                 <Button
//                     size="sm"
//                     borderWidth="2px"
//                     borderRadius="lg"
//                     borderColor={isSameContactFacturation === false ? limitedBorderColor : "gray.200"}
//                     bg={isSameContactFacturation === false ? limitedBgColor : "transparent"}
//                     color={isSameContactFacturation === false ? limitedActiveTextColor : textColor}
//                     _hover={{ borderColor: limitedBorderColor, bg: limitedBgColor, color: limitedActiveTextColor }}
//                     transition="all 0.2s"
//                     onClick={() => handleInvoiceContactChoice(false)}
//                 >
//                     Non
//                 </Button>
//             </HStack>
//             {isSameContactFacturation !== null &&
//                 <Box width="100%">
//                     <form>
//                         <HStack width="100%">
//                             <FormControl isRequired>
//                                 <FormLabel fontSize="1.25em">Nom</FormLabel>
//                                 <G1Input id="ctLastname" name="ctLastname" component="connection.form" disabled={!!isSameContactFacturation} registerInput={{ ...register("ctLastname", { required: true, pattern: /^[a-zàâäéèêëïîôöùûüç]+([ '-][a-zàâäéèêëïîôöùûüç]+)*$/i }) }} />
//                             </FormControl>
//                             <FormControl isRequired>
//                                 <FormLabel fontSize="1.25em">Prénom</FormLabel>
//                                 <G1Input id="ctFirstname" name="ctFirstname" component="connection.form" disabled={!!isSameContactFacturation} registerInput={{ ...register("ctFirstname", { required: true, pattern: /^[a-zàâäéèêëïîôöùûüç]+([ '-][a-zàâäéèêëïîôöùûüç]+)*$/i }) }} />
//                             </FormControl>
//                         </HStack>
//                         <FormControl isRequired mt={2}>
//                             <FormLabel fontSize="1.25em">Adresse mail</FormLabel>
//                             <G1Input id="ctEmail" name="ctEmail" type="email" component="connection.form" placeholder="test@test.com" disabled={!!isSameContactFacturation} registerInput={{ ...register("ctEmail", { required: true, pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i }) }} />
//                         </FormControl>
//                     </form>
//                 </Box>
//             } */}
//         </VStack>
//     );

//     const accountFormElement = (
//         <form>
//             <VStack>
//                 <HStack width="100%">
//                     <FormControl isRequired>
//                         <FormLabel fontSize="1.25em">Nom</FormLabel>
//                         <G1Input id="lastname" name="lastname" component="connection.form" autoFocus textTransform="uppercase" registerInput={{ ...register("lastname", { required: true, pattern: /^[a-zàâäéèêëïîôöùûüç]+([ '-][a-zàâäéèêëïîôöùûüç]+)*$/i }) }} />
//                     </FormControl>
//                     <FormControl isRequired>
//                         <FormLabel fontSize="1.25em">Prénom</FormLabel>
//                         <G1Input id="firstname" name="firstname" component="connection.form" textTransform="capitalize" registerInput={{ ...register("firstname", { required: true, pattern: /^[a-zàâäéèêëïîôöùûüç]+([ '-][a-zàâäéèêëïîôöùûüç]+)*$/i }) }} />
//                     </FormControl>
//                 </HStack>
//                 <FormControl isRequired>
//                     <FormLabel fontSize="1.25em">Adresse mail</FormLabel>
//                     <G1Input id="email" name="email" type="email" component="connection.form" placeholder="test@test.com" textTransform="lowercase" registerInput={{ ...register("email", { required: true, pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i }) }} />
//                 </FormControl>
//                 <CorporationForm
//                     label={isAloneUser ? 'Entreprise' : "Organisation"}
//                     component="connection.form"
//                     required
//                     showUniqueId
//                     countries={countries}
//                     corporationIdentifiersDict={corporationIdentifiersDict}
//                     errors={errors}
//                     register={register}
//                     onChange={handleCorporationChange}
//                     onCountryChange={handleCountryChange} />
//             </VStack>
//         </form>
//     );

//     const confirmationElement = (
//         <>
//             <VStack spacing={6} align="stretch">
//                 <RadioGroup value={selectedPaymentMethod} onChange={setSelectedPaymentMethod}>
//                     <HStack spacing={4} align="stretch">
//                         {paymentMethods?.map((method: MollieMethod) => (
//                             <Box
//                                 key={method.id}
//                                 flex="1"
//                                 p={4}
//                                 borderWidth={2}
//                                 borderRadius="md"
//                                 borderColor={selectedPaymentMethod === method.id ? 'blue.500' : 'gray.200'}
//                                 bg={selectedPaymentMethod === method.id ? 'blue.50' : 'transparent'}
//                                 color={selectedPaymentMethod === method.id ? 'black' : 'gray.100'}
//                                 cursor="pointer"
//                                 _hover={{ borderColor: 'blue.300' }}
//                                 transition="all 0.2s"
//                                 onClick={() => setSelectedPaymentMethod(method.id)}
//                             >
//                                 <HStack spacing={4} justifyContent="center">
//                                     <Radio value={method.id} />
//                                     <Image src={method.image.svg} alt={method.description} maxH="40px" objectFit="contain" />
//                                     <Text fontWeight="bold">{method.id === 'creditcard' ? "Carte bancaire" : (method.id === 'banktransfer' ? "Virement SEPA" : "Pay By Bank")}</Text>
//                                 </HStack>
//                             </Box>
//                         ))}
//                     </HStack>
//                 </RadioGroup>

//                 {selectedPaymentMethod === "creditcard" && (
//                     <MollieCardForm
//                         profileId="pfl_poRPUGvkqS"
//                         planLabel={`${selectedSubscription === "limited" ? "Abonnement 60" : "Abonnement illimité"} · ${amount}€ HT/an`}
//                         testMode
//                         onSuccess={handlePaymentSuccess} />
//                 )}
//                 {selectedPaymentMethod === "banktransfer" && (
//                     <MollieMandateForm
//                         companyName={config.app.title}
//                         planLabel={`${selectedSubscription === "limited" ? "Abonnement 60" : "Abonnement illimité"} · ${amount}€ HT/an`}
//                         onSuccess={handlePaymentSuccess} />
//                 )}
//             </VStack>
//         </>
//     );

//     const steps: { key: Step; label: string; element: JSX.Element }[] = [
//         { key: "subscription", label: "Choix de votre abonnement",  element: selectSubscriptionTypeElement },
//         { key: "account",      label: "Informations du compte",     element: accountFormElement },
//         { key: "billing",      label: "Coordonnées de facturation", element: billingFormElement },
//         { key: "confirmation", label: "Confirmation",               element: confirmationElement },
//     ];

//     const StepSummary = ({ step }: { step: Step }) => {
//         switch (step) {
//             case "subscription":
//                 return (
//                     <VStack align="flex-start" spacing={1} px={6} pb={4} fontSize="sm">
//                         <HStack>
//                             <Text fontWeight="semibold" color="gray.500">Abonnement :</Text>
//                             <Text>{selectedSubscription === "limited" ? "60" : "Illimité"}</Text>
//                             <Text fontWeight="semibold" color="gray.700">
//                                 {selectedSubscription === "limited" ? "109€ HT/an" : "180€ HT/an"}
//                             </Text>
//                         </HStack>
//                         {isAloneUser === false && nbCollaborators > 0 && (
//                             <HStack>
//                                 <Text fontWeight="semibold" color="gray.500">Collaborateurs :</Text>
//                                 <Text>{nbCollaborators} × 60€ HT</Text>
//                             </HStack>
//                         )}
//                         {$length(partnerCode) > 0 && (
//                             <HStack>
//                                 <Text fontWeight="semibold" color="gray.500">Code partenaire :</Text>
//                                 <Text>{partnerCode}</Text>
//                             </HStack>
//                         )}
//                         <Divider />
//                         <HStack>
//                             <Text fontWeight="bold" color="gray.600">Total :</Text>
//                             <Text fontWeight="bold" color="green.500">{amount}€ HT/an</Text>
//                         </HStack>
//                     </VStack>
//                 );

//             case "account":
//                 return (
//                     <VStack align="flex-start" spacing={1} px={6} pb={4} fontSize="sm">
//                         <HStack>
//                             <Text fontWeight="semibold" color="gray.500">Nom :</Text>
//                             <Text>{getValues("lastname")} {getValues("firstname")}</Text>
//                         </HStack>
//                         <HStack>
//                             <Text fontWeight="semibold" color="gray.500">Email :</Text>
//                             <Text>{getValues("email")}</Text>
//                         </HStack>
//                         <HStack>
//                             <Text fontWeight="semibold" color="gray.500">Organisation :</Text>
//                             <Text>{getValues("organizationName")}</Text>
//                         </HStack>
//                         {$ok(getValues("organizationIdentifier")) && (
//                             <HStack>
//                                 <Text fontWeight="semibold" color="gray.500">Identifiant :</Text>
//                                 <Text>{getValues("organizationIdentifier")}</Text>
//                             </HStack>
//                         )}
//                     </VStack>
//                 );

//             case "billing":
//                 return (
//                     <VStack align="flex-start" spacing={1} px={6} pb={4} fontSize="sm">
//                         {/* <HStack>
//                             <Text fontWeight="semibold" color="gray.500">Nom :</Text>
//                             <Text>{getValues("ctLastname")} {getValues("ctFirstname")}</Text>
//                         </HStack>
//                         <HStack>
//                             <Text fontWeight="semibold" color="gray.500">Email :</Text>
//                             <Text>{getValues("ctEmail")}</Text>
//                         </HStack> */}
//                         <HStack>
//                             <Text fontWeight="semibold" color="gray.500">TVA intracommunautaire :</Text>
//                             <Text>{tvaIntra}</Text>
//                         </HStack>
//                     </VStack>
//                 );

//             default:
//                 return null;
//         }
//     };    

//     useEffect(() => {
//        const country = TSCountry.country(Countries.FR)?.alpha2Code ;
//         if ($ok(country)) {
//             const newDict = availableCorporationIdentifiers?.get(country!) ;
//             if ($ok(newDict)) { 
//                 setCorporationIdentifiersDict(newDict!) ;
//             }
//         }
//     }, [availableCorporationIdentifiers]);

//     return (
//         <Box py="5%">
//             <Box color={$g1Color('connection.logo.write')} bg={$g1Color('connection.logo.bg')} paddingTop="8" paddingBottom="2" px="8" maxW="3xl" mx="auto" rounded={{ base: "0", md: "2xl" }}>
//                 <VStack spacing="4" align="stretch">
//                     <Stack direction="row" alignItems="center" spacing="2">
//                         <Image src={$g1IconURL('connection.logo.iconurl')} alt="Application logo" boxSize="72px" />
//                         <Heading as="h1" textAlign="center" size="lg" fontWeight="bold">
//                             {config.app.title}
//                         </Heading>
//                     </Stack>

//                    {steps.map(({ key, label, element }, index) => {
//                         const isCompleted = completedSteps.has(key);
//                         const isActive = currentStep === key;
//                         const isLocked = !isActive && !isCompleted;

//                         return (
//                             <Box key={key} width="100%" borderWidth="2px" borderRadius="lg"
//                                  borderColor={isCompleted ? "green.300" : isActive ? "blue.400" : "gray.200"}
//                                  bg={$g1Color('connection.form.bg')} color={$g1Color('connection.form.write')}
//                                  opacity={isLocked ? 0.5 : 1}
//                                  transition="all 0.3s"
//                             >
//                                 <HStack px={6} py={4} cursor={isCompleted ? "pointer" : "default"}
//                                         onClick={() => isCompleted && setCurrentStep(key)}
//                                         justify="space-between"
//                                 >
//                                     <HStack spacing={3}>
//                                         <Box borderRadius="full" w={6} h={6} display="flex" alignItems="center" justifyContent="center"
//                                              bg={isCompleted ? "green.400" : isActive ? "blue.400" : "gray.300"}
//                                              color="white" fontSize="xs" fontWeight="bold"
//                                         >
//                                             {isCompleted ? <G1Icon.Check /> : index + 1}
//                                         </Box>
//                                         <Text fontWeight="semibold" fontSize="2xl" color={isActive ? "blue.500" : isCompleted ? "green.500" : "gray.400"}>
//                                             {label}
//                                         </Text>
//                                     </HStack>
//                                     {isCompleted && !isActive && <G1Icon.Down />}
//                                 </HStack>

//                                 {isCompleted && !isActive && (
//                                     <StepSummary step={key} />
//                                 )}

//                                 {isActive && (
//                                     <Box px={6} pb={6}>
//                                         {element}
//                                         {key !== "confirmation" && (
//                                             <HStack width="100%" justifyContent="flex-end" mt={4}>
//                                                 <G1Button component="connection" variant="ok" title="Continuer" isDisabled={!validateStep(key)} onClick={() => handleNextStep(key, stepOrder[index + 1])} />
//                                             </HStack>
//                                         )}
//                                         {/* {key === "confirmation" && (
//                                             <HStack width="100%" justifyContent="flex-end" mt={4}>
//                                                 <G1Button component="connection" variant="ok" title="Payer" isDisabled={!$ok(selectedPaymentMethod)} onClick={() => handlePay()} />
//                                             </HStack>
//                                         )} */}
//                                     </Box>
//                                 )}
//                             </Box>
//                         );
//                     })}
//                     <VersionDisplay />
//                 </VStack>
//             </Box>
//         </Box>
//     )
// }
